import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { useAuth } from '../../auth/useAuth';
import { AuthStatus } from '../AuthStatus';
import {
  fetchFullReport,
  fetchMyProfile,
  type FullReportResponse,
  type Profile,
} from '../../lib/apiClient';
import { routes, type WorkbookSpan } from '../../lib/assessment/routes';
import { usePageMeta } from '../../lib/usePageMeta';
import {
  PerspectiveSevenDayWorkbook,
  PerspectiveThirtyDayWorkbook,
  SevenDayWorkbook,
  ThirtyDayWorkbook,
} from './Workbook';

/**
 * Production's workbook titles. Each of the four is its own page there, with
 * its own title, and the title doubles as the document name on the cover.
 */
const WORKBOOK_TITLES: Record<string, string> = {
  'bottleneck:7-day': '7-Day Reset Journal',
  'bottleneck:30-day': '30-Day Reset Workbook',
  'perspective:7-day': '7-Day Perspective Reset Workbook',
  'perspective:30-day': '30-Day Perspective Practice Workbook',
};

/**
 * The Perspective workbooks word their loading and locked states; the
 * Bottleneck pair do not, so those fall back to V2's neutral sentences.
 */
const PERSPECTIVE_LOCKED =
  'This workbook requires a completed Perspective report purchase. Return to the dashboard and your workbook will be available once the report is unlocked.';

type PageState =
  | { phase: 'loading' }
  | { phase: 'locked'; assessmentType: string }
  | { phase: 'not-found' }
  | { phase: 'error' }
  | { phase: 'ok'; data: FullReportResponse };

/**
 * A workbook on its own page (C1).
 *
 * This adds no content and no data. The plans rendered here are the same values
 * the paid report already renders inline, fetched through the same gated
 * `GET /api/reports/:id/full` and drawn by the same two components, so the two
 * surfaces cannot drift.
 *
 * Legacy served four routes because it had a separate page per assessment type.
 * V2 dispatches on `assessmentType` from the payload, as `/reports/:id` already
 * does, so two patterns carry all four workbooks.
 *
 * Work Fit has no workbook in legacy and none here: it resolves to `not-found`
 * rather than an empty page, so a guessed URL says nothing about what exists.
 *
 * Unlike `ReportPrint`, this never calls `printReport()`. Legacy's workbook
 * pages do not auto-print either.
 */
export function WorkbookPage({ id, span }: { id: string; span: WorkbookSpan }) {
  const { status, session } = useAuth();
  // Gated on `status` for the reason recorded on `ReportPage`: a
  // password-recovery session carries a token but is not signed in.
  const token = status === 'signed-in' ? (session?.access_token ?? null) : null;
  const [state, setState] = useState<PageState>({ phase: 'loading' });
  /**
   * Which of production's four workbooks this is, once the payload says. Before
   * that the id alone cannot tell us, so the meta stays neutral.
   */
  const workbookKey =
    state.phase === 'ok' ? `${state.data.report.assessmentType}:${span}` : '';
  const workbookTitle = WORKBOOK_TITLES[workbookKey] ?? null;

  usePageMeta({
    title: workbookTitle
      ? `${workbookTitle} | High Performer OS`
      : 'Your Workbook | High Performer OS',
    noindex: true,
  });
  /** Production prints "Prepared for {name}" on the cover. */
  const [profile, setProfile] = useState<Profile | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void (async () => {
      // A failed name lookup is not a failed workbook: the line is omitted.
      const res = await fetchMyProfile(token).catch(() => null);
      if (cancelled) return;
      setProfile(res?.state === 'ok' ? res.profile : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (status === 'loading' || !token) return;
    let cancelled = false;
    setState({ phase: 'loading' });

    void (async () => {
      const res = await fetchFullReport(token, id);
      if (cancelled) return;
      if (res.state === 'ok') setState({ phase: 'ok', data: res.data });
      else if (res.state === 'not-found') setState({ phase: 'not-found' });
      else if (res.state === 'locked')
        setState({ phase: 'locked', assessmentType: res.assessmentType ?? '' });
      else setState({ phase: 'error' });
    })();

    return () => {
      cancelled = true;
    };
  }, [id, token, status]);

  if (status !== 'signed-in') {
    return (
      <section className="flex flex-col gap-3">
        <p className="text-foreground">Sign in to view your workbook.</p>
        <AuthStatus />
      </section>
    );
  }

  if (state.phase === 'loading')
    return (
      <p className="text-muted-foreground" data-testid="workbook-loading">
        {/*
          Production names the document it is preparing, but each of its four
          workbooks is its own page there. V2 serves one route, and before the
          payload lands the id alone cannot say which workbook this is, so the
          neutral sentence stands rather than a guess.
        */}
        Loading your workbook…
      </p>
    );
  if (state.phase === 'not-found')
    return (
      <p className="text-foreground" data-testid="workbook-not-found">
        You do not have access to this workbook.
      </p>
    );
  if (state.phase === 'error')
    return <p className="text-destructive">Your workbook could not be loaded.</p>;

  if (state.phase === 'locked') {
    return (
      <section className="flex flex-col gap-4" data-testid="workbook-locked">
        {/* Production heads the Bottleneck refusal differently. */}
        <h1 className="text-2xl font-semibold">
          {state.assessmentType === 'perspective'
            ? 'This workbook is locked'
            : 'Workbook Not Available'}
        </h1>
        <p className="text-foreground">
          {state.assessmentType === 'perspective'
            ? PERSPECTIVE_LOCKED
            : 'Please go back and ensure your report is unlocked.'}
        </p>
        <Link
          href={routes.report(id)}
          className="text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          Back to your report
        </Link>
      </section>
    );
  }

  const { report, result } = state.data;
  const preparedFor = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim();
  const completedDate = result.completedAt
    ? new Date(result.completedAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  // Work Fit carries no workbook. Hidden rather than refused, matching the
  // resource-hiding semantics the rest of V2 uses for things you may not have.
  if (report.assessmentType === 'work_fit') {
    return <p className="text-foreground">Workbook not found.</p>;
  }

  /*
    Production's own workbook titles. They are four different documents and it
    names them four different ways: the Bottleneck 7-day is a *journal*, its
    30-day is a *workbook*, and the Perspective pair are a reset and a practice.
    V2 called them "Your 7-day plan" and the like.
  */
  const title = workbookTitle ?? 'Your Workbook';
  const { body } =
    report.assessmentType === 'bottleneck'
      ? span === '7-day'
        ? { body: <SevenDayWorkbook plan={report.content.sevenDayPlan} /> }
        : { body: <ThirtyDayWorkbook plan={report.content.thirtyDayPlan} closing /> }
      : span === '7-day'
        ? {
            /*
              Production's Perspective workbook is a fixed programme, not the
              report's plan rendered as a workbook. The personalised plan is
              shown beneath it, which is how production has it.
            */
            body: (
              <div className="flex flex-col gap-4">
                <PerspectiveSevenDayWorkbook
                  activeTraps={report.interpretationTraps.filter((t) => t.active)}
                  dominantPattern={result.primaryType}
                  immediateReframe={report.content.immediateReframe}
                />
                <SevenDayWorkbook plan={report.sevenDayReset} />
              </div>
            ),
          }
        : {
            body: (
              <div className="flex flex-col gap-4">
                <PerspectiveThirtyDayWorkbook
                  dominantPattern={result.primaryType}
                  immediateReframe={report.content.immediateReframe}
                />
                <ThirtyDayWorkbook plan={report.thirtyDayPractice} />
              </div>
            ),
          };

  return (
    <div className="flex flex-col gap-6" data-testid="workbook-page">
      <nav className="flex flex-wrap items-center justify-between gap-4 print:hidden">
        <div className="flex flex-wrap items-center gap-4">
          <Link
            href={routes.report(id)}
            data-testid="workbook-back-report"
            className="text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            Back to Paid Report
          </Link>
          <Link
            href={routes.dashboard}
            data-testid="workbook-back-dashboard"
            className="text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            Dashboard
          </Link>
        </div>
      </nav>

      {/* Production's cover block: who it is for, when it was taken, what it is. */}
      <header
        className="flex flex-col gap-2 rounded-xl border border-border bg-card p-6"
        data-testid="workbook-cover"
      >
        <h1 className="text-3xl font-bold">{title}</h1>
        <p className="text-lg font-semibold text-muted-foreground">{result.primaryType}</p>
        <dl className="mt-2 flex flex-col gap-1 text-sm text-muted-foreground">
          {preparedFor ? (
            <div className="flex gap-2">
              <dt className="font-bold text-foreground">Prepared for</dt>
              <dd data-testid="workbook-prepared-for">{preparedFor}</dd>
            </div>
          ) : null}
          {completedDate ? (
            <div className="flex gap-2">
              <dt className="font-bold text-foreground">Completed</dt>
              <dd>{completedDate}</dd>
            </div>
          ) : null}
          <div className="flex gap-2">
            <dt className="font-bold text-foreground">Document</dt>
            <dd>{title}</dd>
          </div>
        </dl>
      </header>

      {body}
    </div>
  );
}
